import { Alert, Button, FormControl, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TaskTypeProps } from "./TaskTypeProps";
import styles from "../Task.module.scss";
import stylesDialogs from "../TaskDialogs.module.scss";
import SpinnerArea from "../../../../components/SpinnerArea";
import { useContext, useState } from "react";
import * as Yup from "yup";
import { DocumentApprovalDto } from "../../../../models/task.model";
import { useFormik } from "formik";
import MainContext from "../../../../context/mainContext";
import { ConfirmDialog, SaveSuccessDialog } from "../TaskDialogs";
import tasksService from "../../../../services/tasks.service";
import CancelIcon from "@mui/icons-material/Cancel";

const DeleteApproval: React.FC<TaskTypeProps> = ({ taskHeader }) => {
  const { t } = useTranslation();
  const { service } = useContext(MainContext);
  const [approveSuccess, setApproveSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  const [showNoApprovalSection, setShowNoApprovalSection] = useState(false);

  const [showCancelPublication, setShowCancelPublication] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);

  const validationSchema = Yup.object().shape({
    comments: Yup.string().nullable().required(t("requiredField")!).max(256),
  });

  const formik = useFormik<DocumentApprovalDto>({
    initialValues: {
      comments: "",
      taskId: taskHeader.id,
      approved: !showNoApprovalSection,
    },
    onSubmit: () => console.log("submit"),
    validationSchema: validationSchema,
    validateOnChange: true,
    enableReinitialize: true,
  });

  const approve = () => {
    service(
      () =>
        tasksService.approvalDeleteDocument({
          taskId: taskHeader.id,
          approved: true,
        }),
      {
        callback: () => {
          setApproveSuccess(true);
        },
        loader: setSaving,
      }
    );
  };

  const cancelPublication = () => {
    service(() => tasksService.cancelDelete(formik.values), {
      callback: () => {
        setCancelSuccess(true);
      },
      loader: setSaving,
    });
  };

  return (
    <SpinnerArea loading={saving}>
      <form
        className={styles.draftApproval}
        onSubmit={formik.handleSubmit}
        onReset={formik.handleReset}
      >
        {taskHeader.workflowType !== "Creation" && (
          <div className={styles.workflowReason}>
            <p>
              <strong>{t("justificationOfTheDeletion")}</strong>
            </p>
            <p>{taskHeader.workflowReason}</p>
          </div>
        )}
        <p>{t("deleteApprovalMessage")}</p>
        {!showNoApprovalSection && <p>{t("doYouApproveThisDeletion")}</p>}

        {showNoApprovalSection && (
          <>
            <Alert variant="filled" severity="warning" icon={undefined}>
              {t("deleteApprovalTextWarning")}
            </Alert>
            <FormControl fullWidth>
              <TextField
                id="comments"
                name="comments"
                placeholder={t("justifyTheAnswer")!}
                fullWidth
                multiline
                rows={3}
                value={formik.values.comments || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={formik.touched.comments && formik.errors.comments}
                error={
                  formik.touched.comments && Boolean(formik.errors.comments)
                }
              ></TextField>
            </FormControl>
          </>
        )}
        <div
          className={`${styles.buttonsSection} ${
            showNoApprovalSection ? styles.noApproved : ""
          }`}
        >
          {!showNoApprovalSection ? (
            <>
              <Button className={styles.yes} onClick={approve}>
                {t("yes")}
              </Button>
              <Button
                className={styles.no}
                onClick={() => {
                  setShowNoApprovalSection(true);
                }}
              >
                {t("no")}
              </Button>
            </>
          ) : (
            <>
              <Button
                className={styles.cancel}
                onClick={ () => setShowNoApprovalSection(false)}
              >
                {t("cancel")}
              </Button>
              <Button
                className={styles.cancelPublication}
                disabled={!formik.dirty || !formik.isValid}
                onClick={() => setShowCancelPublication(true)}
              >
                {t("cancelPublication")}
              </Button>
            </>
          )}
        </div>
      </form>
      <SaveSuccessDialog
        open={approveSuccess}
        onAccept={() => {}}
        onClose={() => setApproveSuccess(false)}
        title={t("saveSuccess")}
      />
      <ConfirmDialog
        open={showCancelPublication}
        onClose={() => setShowCancelPublication(false)}
        onAccept={cancelPublication}
        text={t("confirmCancelPublication")}
      />
      <SaveSuccessDialog
        open={cancelSuccess}
        title={t("thePublicationWasCanceled")}
        icon={<CancelIcon />}
        onAccept={() => {}}
        onClose={() => {}}
        className={stylesDialogs.cancelPublicationDialog}
      />
    </SpinnerArea>
  );
};

export default DeleteApproval;
